import React from 'react';
import { Form } from 'react-bootstrap';
import Select from 'react-select';
import type { MultiValue } from 'react-select';

import { CreateUpdateEmailConfigDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import {
  EmailConfigTypeEnum,
  EmailConfigTypeObj,
} from '../../../common/pokCore/validation/enums';
import FormRow from '../../../app/components/FormRow';
import { Option } from '../Selects/Selector';
import CompanySelector from '../Selects/CompanySelector';
import TeamSelector from '../Selects/TeamSelector';
import { useTranslation } from '../../../common/hooks/useTranslation';

interface EmailConfigEditorProps {
  emailConfig: CreateUpdateEmailConfigDto;
  readOnly: boolean;
  propertyChange: (obj: Partial<CreateUpdateEmailConfigDto>) => void;
  createErrors: (errors: string[]) => void;
}

export const EmailConfigFrom: React.FC<EmailConfigEditorProps> = props => {
  const { t, tk } = useTranslation('administrationLng');

  const typeOptions = Object.entries(EmailConfigTypeEnum)
    .map(([key, value]) => ({
      value: key as EmailConfigTypeEnum,
      label: t(EmailConfigTypeObj[value]),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const onChangeTeams = (
    option: Option | MultiValue<Option> | null | undefined,
  ) => {
    if (option && Array.isArray(option)) {
      props.propertyChange({
        teamIds: (option as Option[]).map(({ value }) => value),
      });
    }
  };

  return (
    <Form className="d-grid gap-3">
      <FormRow controlId="type" label={t(tk.administration.mailConfigType)}>
        <Select
          className="full-width"
          value={
            props.emailConfig.type
              ? typeOptions.find(
                  ({ value }) => props.emailConfig.type === value,
                )
              : null
          }
          options={typeOptions}
          onChange={option =>
            props.propertyChange({
              type: option?.value,
            })
          }
          isDisabled={props.readOnly}
          placeholder={t(tk.component.pointOut)}
        />
      </FormRow>
      <FormRow controlId="emails" label={t(tk.administration.emails)}>
        <Form.Control
          type="text"
          value={props.emailConfig.emails.join(',')}
          onChange={e =>
            props.propertyChange({
              emails: e.target.value.split(','),
            })
          }
          readOnly={props.readOnly}
          placeholder="jan@kowalski.pl, adam@nowak.pl, mateusz@wojcik.pl"
        />
      </FormRow>
      <FormRow controlId="company" label={t(tk.common.company)}>
        <CompanySelector
          readOnly={props.readOnly}
          value={props.emailConfig.companyId}
          onChange={option =>
            props.propertyChange({
              companyId: (option as Option)?.value,
              teamIds: [],
            })
          }
          clearable
        />
      </FormRow>
      <FormRow controlId="team" label={t(tk.common.teams)}>
        <TeamSelector
          isMulti={true}
          value={props.emailConfig.teamIds}
          companyId={props.emailConfig.companyId}
          onChange={options => onChangeTeams(options)}
          clearable
        />
      </FormRow>
      <FormRow controlId="active" label={t(tk.common.activeHe)}>
        <Form.Check
          type="checkbox"
          className="pt-2"
          checked={props.emailConfig?.active ?? true}
          onChange={e => props.propertyChange({ active: e.target.checked })}
        />
      </FormRow>
    </Form>
  );
};
